import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Workflow,
  Palette,
  BarChart3,
  ShoppingBag,
  Share2,
  Server,
  Cpu,
  Users,
  ChevronRight,
} from "lucide-react";
import { useDashboardCandidates } from "../hooks/useDashboard";
import type { TeamCandidateCount } from "../api/dashboard.api";

// ─── Technology-specific SVG Icons ───────────────────────────────────────────

const PythonIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M14.25 2.25c-5.18 0-4.81 2.25-4.81 2.25l.01 2.33h4.89v.7h-6.84c-3.77 0-7.07 2.3-7.07 6.8 0 4.5 3.12 6.57 6.84 6.57h1.49v-2.12c0-2.42 2.1-4.48 4.53-4.48h4.52c1.99 0 3.63-1.63 3.63-3.62V6.75c0-4.5-3.54-4.5-7.19-4.5zm-2.02 1.48a.96.96 0 1 1 0 1.92.96.96 0 0 1 0-1.92zM9.75 21.75c5.18 0 4.81-2.25 4.81-2.25l-.01-2.33H9.66v-.7h6.84c3.77 0 7.07-2.3 7.07-6.8 0-4.5-3.12-6.57-6.84-6.57h-1.49v2.12c0 2.42-2.1 4.48-4.53 4.48H6.19c-1.99 0-3.63 1.63-3.63 3.62v4.5c0 4.5 3.54 4.5 7.19 4.5zm2.02-1.48a.96.96 0 1 1 0-1.92.96.96 0 0 1 0 1.92z" />
  </svg>
);

const JSIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 3h18v18H3V3zm11.5 13.5c0 1.4-.9 2.2-2.3 2.2-1.3 0-2.1-.7-2.4-1.6l1.3-.8c.2.5.5.9 1.1.9.5 0 .8-.3.8-.8v-4.9h1.5v5zm4.8 0c0 1.5-1.1 2.2-2.6 2.2-1.4 0-2.3-.7-2.7-1.7l1.3-.7c.3.6.7 1 1.4 1 .6 0 1-.3 1-.7 0-.5-.3-.7-1.1-1l-.5-.2c-1.4-.5-2.2-1.2-2.2-2.4 0-1.4 1.1-2.2 2.5-2.2 1.2 0 2 .5 2.5 1.4l-1.2.8c-.3-.5-.6-.7-1.2-.7-.5 0-.9.3-.9.7 0 .4.3.6 1 .9l.5.2c1.6.6 2.2 1.3 2.2 2.4z" />
  </svg>
);

const AndroidIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 18c0 .55.45 1 1 1h1v3c0 .55.45 1 1 1s1-.45 1-1v-3h4v3c0 .55.45 1 1 1s1-.45 1-1v-3h1c.55 0 1-.45 1-1V9H6v9zm-1-8H3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1s1-.45 1-1v-6c0-.55-.45-1-1-1zm14 0c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1s1-.45 1-1v-6c0-.55-.45-1-1-1zM15.53 2.16l1.3-1.3c.2-.2.2-.51 0-.71a.498.498 0 0 0-.7 0l-1.47 1.47A7.957 7.957 0 0 0 12 1c-1.02 0-2.01.19-2.92.54L7.61.07a.498.498 0 0 0-.7 0c-.2.2-.2.51 0 .71l1.3 1.3C6.31 3.29 5 5.51 5 8h14c0-2.49-1.31-4.71-3.47-5.84zM9 5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm6 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
  </svg>
);

const WordPressIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2zm.89 16.48l-2.73-7.92H12l2.67 7.92h-1.78zm-4.32-6.5l-1.8 5.21A8.24 8.24 0 0 1 3.75 12c0-1.87.62-3.6 1.67-4.99l3.15 9.47zm7.34.88l1.7-4.79c.64 1.15.99 2.48.99 3.9 0 1.94-.69 3.72-1.84 5.11l-.85-4.22zM12 3.75c2.6 0 4.9.19 6.25 2.87l-2.47 7.15-2.28-6.62c.33-.02.66-.04.99-.04a.45.45 0 0 0 0-.9h-3.48a.45.45 0 0 0 0 .9c.28 0 .56.02.83.04l-2.3 6.64-1.46-4.37c.38-.02.76-.04 1.14-.04a.45.45 0 0 0 0-.9H7.17a.45.45 0 0 0 0 .9c.35 0 .7.02 1.05.04l2.9 8.64a8.21 8.21 0 0 1-5.62-2.5l5.12-14.8c.45-.03.91-.05 1.38-.05z" />
  </svg>
);

const AngularIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.5L2.5 5.8 4 17.5 12 22l8-4.5 1.5-11.7L12 2.5zm0 3.3l5.2 11.7h-2.1l-1.1-2.7H10l-1.1 2.7H6.8L12 5.8zm1.3 7.3l-1.3-3.2-1.3 3.2h2.6z" />
  </svg>
);

const JavaIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M8.85 16.84c-2.46.59-4.85.9-4.85.9s1.39-.42 2.92-.76c-1.35-.61-2.92-1.74-2.92-2.96 0-1.83 2.88-2.61 5.37-2.61.64 0 1.25.05 1.83.13-1.04.4-1.78.96-1.78 1.63 0 1.07 1.89 1.82 4.41 1.82 2.06 0 3.65-.51 4.22-1.28.19-.25.29-.53.29-.82 0-1.38-2.22-2.12-4.9-2.12-3.8 0-7.39 1.4-7.39 3.92 0 2.21 2.5 3.38 5.75 3.86-1.42.36-2.95.73-2.95.73s1.25-.1 2.03-.23zm7.84-9.33c0 2.65-2.24 3.75-4.48 4.7-1.38.59-2.76.99-4.04 1.34 3.26-.27 6.94-1.25 6.94-3.5 0-1.63-1.92-2.65-4.53-2.65-.55 0-1.1.04-1.63.11.96-.34 1.65-.77 1.65-1.3 0-.85-1.52-1.4-3.5-1.4-1.57 0-2.85.35-2.85.9 0 .34.48.65 1.32.88-2.73.54-4.8 1.68-4.8 3.38 0 2.24 3.32 3.26 6.78 3.26 3.96 0 7.82-1.48 7.82-4.22z" />
  </svg>
);

const PHPIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 3C7.03 3 3 7.03 3 12s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9zm-4.5 12H6V9h2.5c1.1 0 2 .9 2 2s-.9 2-2 2H7.5v2zm.5-3.5H7.5V10.5H8c.3 0 .5.2.5.5s-.2.5-.5.5zm6 3.5h-1.5V9H14c1.1 0 2 .9 2 2s-.9 2-2 2h-1.5v2zm.5-3.5h-.5V10.5H14c.3 0 .5.2.5.5s-.2.5-.5.5zm4.5 3.5h-1.5V9h1.5v2.5H20V9h1.5v6H20v-2.5h-1.5V15z" />
  </svg>
);

const AppleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.2c.67-.82 1.13-1.96.99-3.1-.98.04-2.17.65-2.87 1.47-.62.72-1.16 1.88-1.01 3 .1.01 2.22-.55 2.89-1.37z" />
  </svg>
);

const DotNetIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-4.5 13.5h-1.8V8.5h1.8v7zm2.5 0a1 1 0 1 1 1-1 1 1 0 0 1-1 1zm4.8.2c-1.8 0-3-1.2-3-3.2s1.2-3.2 3-3.2a2.8 2.8 0 0 1 2.6 1.6l-1.4.8a1.5 1.5 0 0 0-1.2-.8c-.8 0-1.4.6-1.4 1.6s.6 1.6 1.4 1.6a1.5 1.5 0 0 0 1.2-.8l1.4.8a2.8 2.8 0 0 1-2.6 1.6z" />
  </svg>
);

// ─── Static icon/color lookup by team name (case-insensitive) ─────────────────

type IconComponent = React.ComponentType<{ className?: string }>;

interface TeamStyle {
  color: string;
  bg: string;
  icon: IconComponent;
}

const TEAM_STYLE_MAP: Record<string, TeamStyle> = {
  python: { color: "text-blue-500", bg: "bg-blue-500/10", icon: PythonIcon },
  qa: { color: "text-emerald-500", bg: "bg-emerald-500/10", icon: ShieldCheck },
  wordpress: {
    color: "text-sky-500",
    bg: "bg-sky-500/10",
    icon: WordPressIcon,
  },
  android: {
    color: "text-green-500",
    bg: "bg-green-500/10",
    icon: AndroidIcon,
  },
  javascript: { color: "text-amber-500", bg: "bg-amber-500/10", icon: JSIcon },
  devops: { color: "text-cyan-500", bg: "bg-cyan-500/10", icon: Workflow },
  "ui/ux": { color: "text-pink-500", bg: "bg-pink-500/10", icon: Palette },
  angular: { color: "text-red-500", bg: "bg-red-500/10", icon: AngularIcon },
  ba: { color: "text-indigo-500", bg: "bg-indigo-500/10", icon: BarChart3 },
  magento: {
    color: "text-orange-500",
    bg: "bg-orange-500/10",
    icon: ShoppingBag,
  },
  ios: {
    color: "text-slate-600 dark:text-slate-300",
    bg: "bg-slate-500/10",
    icon: AppleIcon,
  },
  ".net": {
    color: "text-purple-500",
    bg: "bg-purple-500/10",
    icon: DotNetIcon,
  },
  hubspot: { color: "text-orange-600", bg: "bg-orange-500/10", icon: Share2 },
  java: { color: "text-red-600", bg: "bg-red-500/10", icon: JavaIcon },
  php: { color: "text-violet-500", bg: "bg-violet-500/10", icon: PHPIcon },
  "it/admin": { color: "text-teal-500", bg: "bg-teal-500/10", icon: Server },
  "solution architect": {
    color: "text-blue-600",
    bg: "bg-blue-500/10",
    icon: Cpu,
  },
};

const FALLBACK_STYLE: TeamStyle = {
  color: "text-slate-500",
  bg: "bg-slate-500/10",
  icon: Users,
};

function getTeamStyle(teamName: string): TeamStyle {
  return TEAM_STYLE_MAP[teamName.toLowerCase()] ?? FALLBACK_STYLE;
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="bg-white dark:bg-slate-900 border border-border rounded-xl px-5 py-3.5 shadow-sm animate-pulse flex items-center justify-between">
      <div className="flex items-center space-x-3.5">
        <div className="w-9 h-9 rounded-xl bg-slate-200 dark:bg-slate-700" />
        <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-700" />
      </div>
      <div className="h-4 w-12 rounded bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TeamwiseCandidateProfiles() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useDashboardCandidates();

  const teams = data?.data?.teams ?? [];
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-foreground">Teamwise Profiles</h3>
      </div>

      {isError && (
        <p className="text-sm text-red-500">
          Failed to load team data. Please try again.
        </p>
      )}

      <div className="space-y-2.5">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
          : teams
              .filter((team: TeamCandidateCount) => team.team_id != null)
              .map((team: TeamCandidateCount) => {
                const { color, bg, icon: Icon } = getTeamStyle(team.team_name);
                const count = team.count ?? 0;
                return (
                  <div
                    key={team.team_id}
                    onClick={() =>
                      navigate(
                        `/candidates?team=${encodeURIComponent(team.team_id)}&team_name=${encodeURIComponent(team.team_name || "")}`,
                      )
                    }
                    className="bg-white dark:bg-slate-900 border border-border rounded-xl px-5 py-3.5 shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all flex items-center justify-between cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3.5">
                      <div className={cn("p-2 rounded-xl", bg)}>
                        <Icon className={cn("h-5 w-5", color)} />
                      </div>
                      <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {team.team_name}
                      </span>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <span className="text-sm font-bold text-foreground">
                          {count}
                        </span>
                        <span className="text-xs text-muted-foreground ml-1.5 font-normal">
                          {count === 1 ? "profile" : "profiles"}
                        </span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                );
              })}
      </div>
    </div>
  );
}
